﻿import { highlightCode } from "../lib/shiki-highlighter";

let heavyModulesPromise = null;
let inputShikiTimer = null;
let compileTimer = null;

export function initCssScssOptimizerPage() {
    const root = document.querySelector("[data-scss-tool-page]");
    const pageName = document.body?.dataset?.page;

    if (!root || pageName !== "css-scss-optimizer") return;

    const page = {
        root,
        mode: root.querySelector("[data-scss-tool-mode]"),
        outputStyle: root.querySelector("[data-scss-output-style]"),
        autoFormat: root.querySelector("[data-scss-auto-format]"),
        removeComments: root.querySelector("[data-scss-remove-comments]"),
        keepLicense: root.querySelector("[data-scss-keep-license]"),
        modernTarget: root.querySelector("[data-scss-modern-target]"),

        input: root.querySelector("[data-scss-input]"),
        output: root.querySelector("[data-scss-output]"),

        run: root.querySelector("[data-scss-run]"),
        copy: root.querySelector("[data-scss-copy]"),
        download: root.querySelector("[data-scss-download]"),
        clear: root.querySelector("[data-scss-clear]"),
        sample: root.querySelector("[data-scss-load-sample]"),

        status: root.querySelector("[data-scss-status]"),

        inputTitle: root.querySelector("[data-scss-input-title]"),
        outputTitle: root.querySelector("[data-scss-output-title]"),

        metaInput: root.querySelector("[data-scss-meta-input]"),
        metaOutput: root.querySelector("[data-scss-meta-output]"),
        metaSaved: root.querySelector("[data-scss-meta-saved]"),
        metaLines: root.querySelector("[data-scss-meta-lines]"),

        inputHighlight: root.querySelector("[data-scss-input-highlight]"),
        outputHighlight: root.querySelector("[data-scss-output-highlight]"),
    };

    page.run?.addEventListener("click", () => runScssTool(page, true));

    page.mode?.addEventListener("change", () => {
        syncModeLabels(page);
        loadSample(page, false);

        renderPlainCodePreview(page, "input");
        runScssTool(page, false);
        renderAllShiki(page);
    });

    [
        page.outputStyle,
        page.autoFormat,
        page.removeComments,
        page.keepLicense,
        page.modernTarget
    ].filter(Boolean).forEach(input => {
        input.addEventListener("change", () => runScssTool(page, false));
        input.addEventListener("input", () => runScssTool(page, false));
    });

    page.input?.addEventListener("input", () => {
        renderPlainCodePreview(page, "input");

        window.clearTimeout(inputShikiTimer);
        inputShikiTimer = window.setTimeout(() => {
            renderShikiCode(page, "input");
        }, 220);

        window.clearTimeout(compileTimer);
        compileTimer = window.setTimeout(() => {
            runScssTool(page, false);
        }, 750);
    });

    page.input?.addEventListener("blur", () => {
        window.clearTimeout(inputShikiTimer);
        window.clearTimeout(compileTimer);

        renderShikiCode(page, "input");
        runScssTool(page, false);
    });

    page.copy?.addEventListener("click", async () => {
        await copyOutput(page);
    });

    page.download?.addEventListener("click", () => {
        downloadOutput(page);
    });

    page.clear?.addEventListener("click", () => {
        if (!page.input || !page.output) return;

        page.input.value = "";
        page.output.value = "";

        renderAllShiki(page);
        updateMeta(page);
        setStatus(page, "Ready", "Input cleared.", "info");
    });

    page.sample?.addEventListener("click", () => {
        loadSample(page, true);
        renderPlainCodePreview(page, "input");
        runScssTool(page, true);
        renderAllShiki(page);
    });

    syncModeLabels(page);
    bindCodeEditorScroll(page);

    renderPlainCodePreview(page, "input");
    runScssTool(page, false);

    renderShikiCode(page, "input");
}



function getEditorLanguage(page, target) {
    const mode = page.mode?.value || "scss-to-css";

    if (target === "input") {
        return mode === "css-format" ||
            mode === "css-optimize-minify" ||
            mode === "css-to-scss"
            ? "css"
            : "scss";
    }

    return mode === "css-to-scss" || mode === "scss-format"
        ? "scss"
        : "css";
}

function getHighlightElements(page, target) {
    if (target === "input") {
        return {
            textarea: page.input,
            highlight: page.inputHighlight
        };
    }

    return {
        textarea: page.output,
        highlight: page.outputHighlight
    };
}

function renderPlainCodePreview(page, target) {
    const { textarea, highlight } = getHighlightElements(page, target);

    if (!textarea || !highlight) return;

    highlight.innerHTML = `
        <pre class="shiki scss-code-editor__plain"><code>${escapeHtml(textarea.value || " ")}</code></pre>
    `;

    syncEditorScroll(textarea, highlight);
}

async function renderShikiCode(page, target) {
    const { textarea, highlight } = getHighlightElements(page, target);

    if (!textarea || !highlight) return;

    try {
        const html = await highlightCode(
            textarea.value || " ",
            getEditorLanguage(page, target)
        );

        highlight.innerHTML = html;
        syncEditorScroll(textarea, highlight);
    } catch {
        renderPlainCodePreview(page, target);
    }
}

function scheduleInputShiki(page) {
    window.clearTimeout(inputShikiTimer);

    inputShikiTimer = window.setTimeout(() => {
        renderShikiCode(page, "input");
    }, 220);
}

function scheduleCompile(page) {
    window.clearTimeout(compileTimer);

    compileTimer = window.setTimeout(() => {
        runScssTool(page, false);
    }, 700);
}

function syncEditorScroll(textarea, highlight) {
    if (!textarea || !highlight) return;

    const shikiPre = highlight.querySelector("pre");

    if (!shikiPre) return;

    shikiPre.scrollTop = textarea.scrollTop;
    shikiPre.scrollLeft = textarea.scrollLeft;
}

function bindCodeEditorScroll(page) {
    [
        [page.input, page.inputHighlight],
        [page.output, page.outputHighlight]
    ].forEach(([textarea, highlight]) => {
        if (!textarea || !highlight) return;

        textarea.addEventListener("scroll", () => {
            syncEditorScroll(textarea, highlight);
        });
    });
}

async function renderAllShiki(page) {
    await renderShikiCode(page, "input");
    await renderShikiCode(page, "output");
}

async function getHeavyModules() {
    if (heavyModulesPromise) return heavyModulesPromise;

    heavyModulesPromise = Promise.all([
        import("sass"),
        import("csso"),
        import("postcss"),
        import("postcss-selector-parser"),
        import("prettier/standalone"),
        import("prettier/plugins/postcss")
    ]).then(([sass, csso, postcss, selectorParser, prettier, prettierPostcss]) => ({
        sass: sass.default ?? sass,
        csso: csso.default ?? csso,
        postcss: postcss.default ?? postcss,
        selectorParser: selectorParser.default ?? selectorParser,
        prettier: prettier.default ?? prettier,
        prettierPostcss: prettierPostcss.default ?? prettierPostcss
    }));

    return heavyModulesPromise;
}

async function runScssTool(page, animate) {
    if (!page.input || !page.output) return;

    const mode = page.mode?.value || "scss-to-css";
    const input = page.input.value || "";

    if (!input.trim()) {
        page.output.value = "";
        updateMeta(page);
        setStatus(page, "Ready", "Paste CSS or SCSS code to start.", "info");
        return;
    }

    setStatus(page, "Processing", "Loading compiler modules and transforming code...", "loading");

    try {
        const modules = await getHeavyModules();
        const options = getOptions(page);

        let output = "";

        switch (mode) {
            case "scss-to-css":
                output = await compileScssToCss(input, modules, {
                    ...options,
                    minify: false
                });
                break;

            case "scss-to-css-minify":
                output = await compileScssToCss(input, modules, {
                    ...options,
                    minify: true
                });
                break;

            case "css-optimize-minify":
                output = await optimizeCss(input, modules, {
                    ...options,
                    minify: true
                });
                break;

            case "css-format":
                output = await formatCode(input, modules, "css");
                break;

            case "scss-format":
                output = await formatCode(input, modules, "scss");
                break;

            case "css-to-scss":
                output = cssToScssLike(input, options, modules);

                if (options.autoFormat) {
                    output = await formatCode(output, modules, "scss");
                }

                break;

            default:
                output = input;
                break;
        }

        page.output.value = output.trim();

        await renderShikiCode(page, "output");
        await renderShikiCode(page, "input");

        if (animate) {
            page.output.classList.remove("is-generated");
            void page.output.offsetWidth;
            page.output.classList.add("is-generated");
        }

        updateMeta(page);
        setStatus(page, "Success", "Conversion completed successfully.", "success");
    } catch (error) {
        page.output.value = "";
        await renderShikiCode(page, "output");

        updateMeta(page);
        setStatus(page, "Error", error?.message || "Conversion failed.", "error");
    }
}


function createScssTreeNode() {
    return {
        declarations: [],
        children: new Map()
    };
}

function insertScssPath(root, parts, declarations) {
    let current = root;

    parts.filter(Boolean).forEach((part, index) => {
        if (!current.children.has(part)) {
            current.children.set(part, createScssTreeNode());
        }

        current = current.children.get(part);

        if (index === parts.length - 1) {
            current.declarations.push(...declarations);
        }
    });
}

function getAtRulePath(rule) {
    const path = [];
    let parent = rule.parent;

    while (parent) {
        if (parent.type === "atrule") {
            path.unshift(`@${parent.name}${parent.params ? ` ${parent.params}` : ""}`);
        }

        parent = parent.parent;
    }

    return path;
}

function parseSelectorPaths(selector, selectorParser) {
    const paths = [];

    const processor = selectorParser(selectors => {
        selectors.each(selectorNode => {
            const path = selectorNodeToScssPath(selectorNode);
            if (path.length) paths.push(path);
        });
    });

    processor.processSync(selector);

    return paths;
}

function selectorNodeToScssPath(selectorNode) {
    const compounds = [];
    let current = [];
    let combinator = " ";

    selectorNode.each(node => {
        if (node.type === "combinator") {
            pushCompound(compounds, current, combinator);
            current = [];
            combinator = normalizeCombinator(node.value);
            return;
        }

        current.push(node);
    });

    pushCompound(compounds, current, combinator);

    const path = [];

    compounds.forEach((compound, index) => {
        const parts = compoundToNestedParts(compound.nodes);

        if (!parts.length) return;

        if (index === 0) {
            path.push(...parts);
            return;
        }

        const first = parts[0];

        if (compound.combinator === ">") {
            path.push(`> ${first}`);
        } else if (compound.combinator === "+") {
            path.push(`+ ${first}`);
        } else if (compound.combinator === "~") {
            path.push(`~ ${first}`);
        } else {
            path.push(first);
        }

        path.push(...parts.slice(1));
    });

    return path;
}

function pushCompound(compounds, nodes, combinator) {
    if (!nodes.length) return;

    compounds.push({
        combinator,
        nodes: [...nodes]
    });
}

function normalizeCombinator(value) {
    const normalized = String(value || " ").trim();

    if (normalized === ">") return ">";
    if (normalized === "+") return "+";
    if (normalized === "~") return "~";

    return " ";
}

function compoundToNestedParts(nodes) {
    if (!nodes.length) return [];

    const nodeStrings = nodes.map(node => node.toString());
    const baseIndex = findBaseNodeIndex(nodes);

    if (baseIndex < 0) {
        return [nodeStrings.join("")];
    }

    const base = nodeStrings[baseIndex];

    const beforeBase = nodeStrings.slice(0, baseIndex).join("");
    const afterBaseNodes = nodes.slice(baseIndex + 1);

    const parts = [];

    if (beforeBase) {
        parts.push(beforeBase);
        parts.push(base);
    } else {
        parts.push(base);
    }

    afterBaseNodes.forEach(node => {
        const text = node.toString();

        if (!text) return;

        if (node.type === "pseudo" ||
            node.type === "class" ||
            node.type === "id" ||
            node.type === "attribute") {
            parts.push(`&${text}`);
            return;
        }

        parts.push(text);
    });

    return parts;
}

function findBaseNodeIndex(nodes) {
    const preferred = ["class", "id", "tag", "universal", "attribute"];

    for (const type of preferred) {
        const index = nodes.findIndex(node => node.type === type);
        if (index >= 0) return index;
    }

    return 0;
}

function getOptions(page) {
    return {
        outputStyle: page.outputStyle?.value || "expanded",
        autoFormat: Boolean(page.autoFormat?.checked),
        removeComments: Boolean(page.removeComments?.checked),
        keepLicense: Boolean(page.keepLicense?.checked),
        modernTarget: Boolean(page.modernTarget?.checked)
    };
}

async function compileScssToCss(input, modules, options) {
    const result = modules.sass.compileString(input, {
        style: options.minify || options.outputStyle === "compressed"
            ? "compressed"
            : "expanded",
        sourceMap: false
    });

    let css = result.css || "";

    if (options.removeComments) {
        css = removeCssComments(css, options.keepLicense);
    }

    if (options.minify) {
        css = await optimizeCss(css, modules, {
            ...options,
            minify: true
        });
    } else if (options.autoFormat && options.outputStyle !== "compressed") {
        css = await formatCode(css, modules, "css");
    }

    return css;
}

async function optimizeCss(input, modules, options) {
    let css = input;

    if (options.removeComments) {
        css = removeCssComments(css, options.keepLicense);
    }

    const minifyOptions = {
        restructure: true,
        comments: options.keepLicense ? "exclamation" : false
    };

    let output = modules.csso.minify(css, minifyOptions).css;

    if (!options.minify && options.autoFormat) {
        output = await formatCode(output, modules, "css");
    }

    return output;
}

async function formatCode(input, modules, parser) {
    return await modules.prettier.format(input, {
        parser,
        plugins: [modules.prettierPostcss],
        printWidth: 100,
        tabWidth: 4,
        useTabs: false,
        singleQuote: false,
        bracketSpacing: true
    });
}

function removeCssComments(css, keepLicense) {
    if (keepLicense) {
        const licenses = [];
        let index = 0;

        const withoutLicensePlaceholders = css.replace(/\/\*![\s\S]*?\*\//g, match => {
            const token = `___VIWO_LICENSE_COMMENT_${index++}___`;
            licenses.push({ token, value: match });
            return token;
        });

        let cleaned = withoutLicensePlaceholders.replace(/\/\*[\s\S]*?\*\//g, "");

        licenses.forEach(item => {
            cleaned = cleaned.replace(item.token, item.value);
        });

        return cleaned;
    }

    return css.replace(/\/\*[\s\S]*?\*\//g, "");
}

function cssToScssLike(css, options, modules) {
    const cleaned = options.removeComments
        ? removeCssComments(css, options.keepLicense)
        : css;

    const root = modules.postcss.parse(cleaned);
    const tree = createScssTreeNode();

    root.walkRules(rule => {
        if (!rule.selector) return;

        const declarations = rule.nodes
            ?.filter(node => node.type === "decl")
            .map(decl => `${decl.prop}: ${decl.value};`) ?? [];

        if (!declarations.length) return;

        const atRulePath = getAtRulePath(rule);
        const selectorPaths = parseSelectorPaths(rule.selector, modules.selectorParser);

        selectorPaths.forEach(selectorPath => {
            insertScssPath(tree, [...atRulePath, ...selectorPath], declarations);
        });
    });

    return renderScssTreeNode(tree, 0).trim();
}

function renderScssTree(tree, level) {
    const indent = "    ".repeat(level);
    let output = "";

    tree.forEach((node, selector) => {
        output += `${indent}${selector} {\n`;

        node.declarations.forEach(declaration => {
            output += `${indent}    ${declaration}\n`;
        });

        if (node.children.size > 0) {
            if (node.declarations.length > 0) output += "\n";
            output += renderScssTree(node.children, level + 1);
        }

        output += `${indent}}\n\n`;
    });

    return output;
}

function syncModeLabels(page) {
    const mode = page.mode?.value || "scss-to-css";

    const map = {
        "scss-to-css": {
            input: "SCSS Code",
            output: "Generated CSS"
        },
        "scss-to-css-minify": {
            input: "SCSS Code",
            output: "Minified CSS"
        },
        "css-optimize-minify": {
            input: "CSS Code",
            output: "Optimized CSS"
        },
        "css-format": {
            input: "CSS Code",
            output: "Formatted CSS"
        },
        "scss-format": {
            input: "SCSS Code",
            output: "Formatted SCSS"
        },
        "css-to-scss": {
            input: "CSS Code",
            output: "SCSS-like Output"
        }
    };

    const selected = map[mode] || map["scss-to-css"];

    if (page.inputTitle) page.inputTitle.textContent = selected.input;
    if (page.outputTitle) page.outputTitle.textContent = selected.output;
}

function loadSample(page, force) {
    if (!page.input) return;
    if (!force && page.input.value.trim()) return;

    const mode = page.mode?.value || "scss-to-css";

    if (mode.includes("scss")) {
        page.input.value = `.product-card {
    padding: 1.25rem;
    border-radius: 18px;
    background: #ffffff;

    &__title {
        font-size: 1.25rem;
        font-weight: 800;
    }

    &__price {
        color: #4f46e5;
        font-weight: 900;
    }

    &:hover {
        transform: translateY(-3px);
    }
}`;
        return;
    }

    page.input.value = `.product-card {
    padding: 1.25rem;
    border-radius: 18px;
    background: #ffffff;
}

.product-card .product-title {
    font-size: 1.25rem;
    font-weight: 800;
}

.product-card .product-price {
    color: #4f46e5;
    font-weight: 900;
}

.product-card:hover {
    transform: translateY(-3px);
}`;
}

function updateMeta(page) {
    const input = page.input?.value || "";
    const output = page.output?.value || "";

    const inputBytes = new Blob([input]).size;
    const outputBytes = new Blob([output]).size;

    const saved = inputBytes > 0
        ? Math.max(0, Math.round(((inputBytes - outputBytes) / inputBytes) * 100))
        : 0;

    const lines = output
        ? output.split(/\r?\n/).length
        : 0;

    if (page.metaInput) page.metaInput.textContent = formatBytes(inputBytes);
    if (page.metaOutput) page.metaOutput.textContent = formatBytes(outputBytes);
    if (page.metaSaved) page.metaSaved.textContent = `${saved}%`;
    if (page.metaLines) page.metaLines.textContent = String(lines);
}

function setStatus(page, title, message, state) {
    if (!page.status) return;

    page.status.dataset.state = state;
    page.status.innerHTML = `
        <span>
            <i class="${getStatusIcon(state)}"></i>
        </span>
        <div>
            <strong>${escapeHtml(title)}</strong>
            <p>${escapeHtml(message)}</p>
        </div>
    `;
}

function getStatusIcon(state) {
    switch (state) {
        case "success":
            return "fa-regular fa-circle-check";
        case "error":
            return "fa-regular fa-triangle-exclamation";
        case "loading":
            return "fa-regular fa-spinner-third fa-spin";
        case "info":
        default:
            return "fa-regular fa-circle-info";
    }
}

async function copyOutput(page) {
    const value = page.output?.value || "";
    if (!value) return;

    try {
        await navigator.clipboard.writeText(value);
        showDone(page.copy, "Copied");
    } catch {
        fallbackCopy(value);
        showDone(page.copy, "Copied");
    }
}

function downloadOutput(page) {
    const value = page.output?.value || "";
    if (!value) return;

    const mode = page.mode?.value || "scss-to-css";
    const ext = mode === "css-to-scss" || mode === "scss-format"
        ? "scss"
        : "css";

    const timestamp = new Date()
        .toISOString()
        .replace(/[:.]/g, "-");

    const blob = new Blob([value], {
        type: "text/plain;charset=utf-8"
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = `style-${timestamp}.${ext}`;

    document.body.appendChild(link);
    link.click();
    link.remove();

    URL.revokeObjectURL(url);
}

function fallbackCopy(value) {
    const textarea = document.createElement("textarea");
    textarea.value = value;
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";

    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand("copy");
    textarea.remove();
}

function showDone(button, text) {
    if (!button) return;

    const old = button.innerHTML;

    button.classList.add("is-done");
    button.innerHTML = `<i class="fa-regular fa-check"></i>${text}`;

    window.setTimeout(() => {
        button.innerHTML = old;
        button.classList.remove("is-done");
    }, 950);
}

function formatBytes(bytes) {
    if (!bytes) return "0 KB";

    const units = ["B", "KB", "MB"];
    let size = bytes;
    let index = 0;

    while (size >= 1024 && index < units.length - 1) {
        size /= 1024;
        index++;
    }

    return `${size.toFixed(index === 0 ? 0 : 1)} ${units[index]}`;
}

function debounce(fn, delay) {
    let timer;

    return (...args) => {
        clearTimeout(timer);
        timer = window.setTimeout(() => fn(...args), delay);
    };
}

function escapeHtml(value) {
    return String(value || "")
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll('"', "&quot;")
        .replaceAll("'", "&#039;");
}